import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable({
  providedIn: 'root'
})
export class PwaService {

  public isMobileRunning = this.isMobileDevice();

  public os = 'android';

  private deferredPrompt: any = null;

  /**
   * Update mobile running state based on current viewport/PWA status
   */
  setMobileRunning(isMobile: boolean): void {
    this.isMobileRunning = isMobile;
  }
  private readonly INSTALLED_FLAG_KEY = 'pwa-installed';
  
  // Separate observables for app component and home component
  private _showInstallInApp = new BehaviorSubject<boolean>(false);
  private _showInstallInHome = new BehaviorSubject<boolean>(false);
  
  showInstallInApp$ = this._showInstallInApp.asObservable();
  showInstallInHome$ = this._showInstallInHome.asObservable();

  constructor(private snackBar: MatSnackBar) {
    this.setupPwaListeners();
  }

  private canShowInstallOptions(): boolean {
    const userAgent = navigator.userAgent || '';
    const hasMatchMedia = typeof window.matchMedia === 'function';
    const isMobileUa = /Android|iPhone|iPad|iPod|Mobile/i.test(userAgent);
    const isCoarsePointer = hasMatchMedia ? window.matchMedia('(pointer: coarse)').matches : false;
    const isIpadDesktopMode = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    
    // Allow Firefox (all versions) to show install options
    const isFirefox = /Firefox|FxiOS/i.test(userAgent);

    return isMobileUa || isCoarsePointer || isIpadDesktopMode || isFirefox;
  }

  private isAppleDevice(): boolean {
    const userAgent = navigator.userAgent || '';
    const platform = navigator.platform || '';
    const isIosDevice = /iPhone|iPad|iPod/i.test(userAgent);
    const isIpadDesktopMode = platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    const isMacSafariLike = /Macintosh/i.test(userAgent) && /Safari/i.test(userAgent) && !/Chrome|CriOS|Edg/i.test(userAgent);

    return isIosDevice || isIpadDesktopMode || isMacSafariLike;
  }

  /**
   * Returns true for browsers that do NOT support the beforeinstallprompt event
   * (Safari/iOS, Firefox, Firefox for iOS). Install button must be shown immediately
   * for these — clicking it will display manual instructions.
   */
  private nativePromptUnsupported(): boolean {
    const ua = navigator.userAgent || '';
    
    // If running in browser mode and app is already installed, don't show install button
  

    const isIos = /iPhone|iPad|iPod|Android/i.test(ua);
    const isIpadDesktopMode = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
    const isSafari = /Safari/i.test(ua) && !/Chrome|CriOS|Edg|Firefox|FxiOS/i.test(ua);
    
    // All Firefox versions (Android, iOS, Desktop) should show install button
    // They don't support beforeinstallprompt event
          // Firefox Android supports PWA installation, but not via beforeinstallprompt
    const isFirefoxAndroid = /Firefox/i.test(ua) && /Android/i.test(ua);
    const isFirefoxDesktop = /Firefox/i.test(ua) && !/Android|Mobile/i.test(ua) && !/Seamonkey/i.test(ua);
    const isFirefoxIOS = /FxiOS/i.test(ua);
    
    const isFirefox = isFirefoxAndroid || isFirefoxDesktop || isFirefoxIOS;


    return isIos || isIpadDesktopMode || isSafari || isFirefox;
  }

  /**
   * Check if user meets requirements to install PWA:
   * 1. User must be logged in (userToken exists)
   * 2. User must have accepted privacy policy (accept_policy = 1)
   */
  private isUserEligibleForInstall(): boolean {
    return true
    const userToken = localStorage.getItem('userToken');
    if (!userToken) {
      return false;
    }

    const currentUserStr = localStorage.getItem('currentUser');
    if (!currentUserStr) {
      return false;
    }

    try {
      const currentUser = JSON.parse(currentUserStr);
      return !!currentUser.accept_policy;
    } catch (e) {
      console.warn('PwaService: Error parsing currentUser from localStorage', e);
      return false;
    }
  }

  /**
   * Get message explaining why user cannot install PWA
   */
  getIneligibilityMessage(): string {
    const userToken = localStorage.getItem('userToken');
    if (!userToken) {
      return 'Musisz być zalogowany, aby zainstalować aplikację. Przejdź do strony logowania.';
    }

    const currentUserStr = localStorage.getItem('currentUser');
    if (!currentUserStr) {
      return 'Musisz być zalogowany, aby zainstalować aplikację. Przejdź do strony logowania.';
    }

    try {
      const currentUser = JSON.parse(currentUserStr);
      if (!currentUser.accept_policy) {
        return 'Musisz zaakceptować politykę prywatności, aby zainstalować aplikację. Przejdź do ustawień profilu i zaakceptuj politykę.';
      }
    } catch (e) {
      // Fall through to default message
    }

    return 'Musisz być zalogowany i zaakceptować politykę prywatności, aby zainstalować aplikację.';
  }

  isMobileDevice(): boolean {
    const userAgent = navigator.userAgent || '';
    console.log('PwaService: User Agent:', userAgent);
    return /Android|iPhone|iPad|iPod|Mobile/i.test(userAgent);
  }

  isSupportedBrowser(): boolean {
    const userAgent = navigator.userAgent || '';
    const isChrome = (/Chrome/i.test(userAgent) || /CriOS/i.test(userAgent)) && !/Edg|OPR|Opera/i.test(userAgent);
    const isEdge = /Edg/i.test(userAgent);
    const isOpera = /OPR|Opera/i.test(userAgent);
    const isFirefox = /Firefox/i.test(userAgent) || /FxiOS/i.test(userAgent);
    const isSafari = /Safari/i.test(userAgent) && !/Chrome|CriOS|Edg|Firefox|FxiOS/i.test(userAgent);
    return isChrome || isEdge || isOpera || isFirefox || isSafari;
  } 

  pwaAlreadyInstalled(): boolean {
    return localStorage.getItem(this.INSTALLED_FLAG_KEY) === 'true';
  }


  isInstalableBrowser(): boolean {
    const userAgent = navigator.userAgent || '';
    const isChrome = (/Chrome/i.test(userAgent) || /CriOS/i.test(userAgent)) && !/Edg|OPR|Opera/i.test(userAgent);
    const isEdge = /Edg/i.test(userAgent);
    const isFirefox = /Firefox/i.test(userAgent) || /FxiOS/i.test(userAgent);
    const isSafari = /Safari/i.test(userAgent) && !/Chrome|CriOS|Edg|Firefox|FxiOS/i.test(userAgent);
    return isChrome || isEdge || isFirefox || isSafari;
  }

  getBrosweerName(): string {
    const userAgent = navigator.userAgent || '';
    if (/Edg/i.test(userAgent)) {
      return 'Edge';
    } else if (/OPR|Opera/i.test(userAgent)) {
      return 'Opera';
    } else if (/Firefox/i.test(userAgent) && /Android/i.test(userAgent)) {
      return 'Firefox na Androidzie';
    } else if (/Firefox/i.test(userAgent) && !/Android|Mobile/i.test(userAgent) && !/Seamonkey/i.test(userAgent)) {
      return 'Firefox na komputerze';
    } else if (/FxiOS/i.test(userAgent)) {
      return 'Firefox na iOS';
    } else if (/Safari/i.test(userAgent) && !/Chrome|CriOS|Edg|Firefox|FxiOS/i.test(userAgent)) {
      return 'Safari';
    } else if ((/Chrome/i.test(userAgent) || /CriOS/i.test(userAgent)) && !/Edg|OPR|Opera/i.test(userAgent)) {
      return 'Chrome';
    } else {
      return 'nieznana przeglądarka';
    }
  }

  

  isPwaInstalled(): boolean {
    // Check URL query parameter first - force mobile mode if mobile=true
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const mobileParam = urlParams.get('mobile');
      const deviceIdParam = urlParams.get('device_id');
      const osParam = urlParams.get('platform');

      console.log('URL: ', urlParams.toString());

      if (osParam) {
        // Save to localStorage for persistence across navigation
        this.os = osParam;
        localStorage.setItem('platform', osParam);
      } else {
        const currentOs = localStorage.getItem('platform');
        if (!currentOs) {
          localStorage.setItem('platform', "web");
        }
      }
      
      if (deviceIdParam?.length > 0) {
        localStorage.setItem('deviceIdParam', deviceIdParam);
      }

      if (mobileParam === 'true') {
        // Save to localStorage for persistence across navigation
        localStorage.setItem('forceMobileMode', 'true');
        return true;
      } else if (mobileParam === 'false') {
        // Allow explicitly disabling mobile mode
        localStorage.removeItem('forceMobileMode');
      }
    } catch (e) {
      console.warn('PwaService: Error parsing URL params:', e);
    }

    // Check localStorage for previously set mobile mode
    if (localStorage.getItem('forceMobileMode') == 'true') {
      return true;
    }

    const hasMatchMedia = typeof window.matchMedia === 'function';
  
    // Explicitly check if running in browser mode - return false immediately
    const isBrowserMode = hasMatchMedia ? window.matchMedia('(display-mode: browser)').matches : false;
    if (isBrowserMode) {
      //return false; // Running in browser, not installed app
    }
  
    // Check various standalone/installed display modes
    const isStandalone = hasMatchMedia ? window.matchMedia('(display-mode: standalone)').matches : false;
    const isFullscreen = hasMatchMedia ? window.matchMedia('(display-mode: fullscreen)').matches : false;
    const isMinimalUi = hasMatchMedia ? window.matchMedia('(display-mode: minimal-ui)').matches : false;
    const isWindowControlsOverlay = hasMatchMedia ? window.matchMedia('(display-mode: window-controls-overlay)').matches : false;
    
    // iOS specific checks
    const isIosStandalone = (window.navigator as any).standalone === true;
    const standaloneFallback = (navigator as any).standalone === true;
    
    // Android TWA (Trusted Web Activity) check
    const isTwa = document.referrer.includes('android-app://');
    
    // Additional check for Apple devices
    const isAppleInstalled = this.isAppleDevice() && (isStandalone || isFullscreen || isMinimalUi || isWindowControlsOverlay || isIosStandalone || standaloneFallback);

    // Check if any standalone condition is met
    const isCurrentlyInStandaloneMode = isStandalone || isFullscreen || isMinimalUi || isWindowControlsOverlay || isIosStandalone || standaloneFallback || isTwa || isAppleInstalled;
    

 
    // Not in browser mode and not in standalone mode - return false
    return isCurrentlyInStandaloneMode;
  }

  private setupPwaListeners(): void {
    if (!this.canShowInstallOptions()) {
      this._showInstallInApp.next(false);
      this._showInstallInHome.next(false);
      return;
    }

    // Check if app is already installed
    if (this.isPwaInstalled()) {
      localStorage.setItem(this.INSTALLED_FLAG_KEY, 'true');
      this._showInstallInApp.next(false);
      this._showInstallInHome.next(false);
      return;
    }

    const homePwaDismissed = localStorage.getItem('home-pwa-dismissed');

    // Safari iOS and Firefox never fire beforeinstallprompt — show the button
    // immediately so users can access manual installation instructions.
    if (this.nativePromptUnsupported()) {
      this._showInstallInApp.next(true);
      if (!homePwaDismissed) {
        this._showInstallInHome.next(true);
      }
      // Still register the beforeinstallprompt listener in case the browser
      // ever gains support, but don't depend on it.
    }

    window.addEventListener('beforeinstallprompt', (e) => {
      if (!this.canShowInstallOptions()) {
        this._showInstallInApp.next(false);
        this._showInstallInHome.next(false);
        return;
      }

      e.preventDefault();
      this.deferredPrompt = e;
      
      if (!this.isPwaInstalled()) {
        // Always show in app component when installation is possible
        this._showInstallInApp.next(true);
        
        // Show in home component only if not dismissed
        const homePwaDismissed = localStorage.getItem('home-pwa-dismissed');
        
        if (!homePwaDismissed) {
          this._showInstallInHome.next(true);
        }
      }
    });

    window.addEventListener('appinstalled', () => {
      localStorage.setItem(this.INSTALLED_FLAG_KEY, 'true');
      this._showInstallInApp.next(false);
      this._showInstallInHome.next(false);
      localStorage.setItem('home-pwa-dismissed', 'true'); // Mark as installed
    });
  }

  async installPwa(): Promise<boolean> {
    // Check user eligibility first
    if (!this.isUserEligibleForInstall()) {
      this.snackBar.open(this.getIneligibilityMessage(), 'Zamknij', {
        duration: 6000,
        panelClass: ['warning-snackbar'],
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      });
      return false;
    }

    // Native prompt is available (Chrome, Edge, Opera)
    if (this.deferredPrompt && typeof this.deferredPrompt.prompt === 'function') {
      try {
        this.deferredPrompt.prompt();
        const choiceResult = await this.deferredPrompt.userChoice;
        
        if (choiceResult.outcome === 'accepted') {
          localStorage.setItem(this.INSTALLED_FLAG_KEY, 'true');
          this._showInstallInApp.next(false);
          this._showInstallInHome.next(false);
          localStorage.setItem('home-pwa-dismissed', 'true');
          return true;
        } else {
          // User declined - don't affect app component, only hide home component temporarily
          this._showInstallInHome.next(false);
          return false;
        }
      } catch (error) {
        console.error('PWA Service: Error with installation:', error);
        return false;
      } finally {
        // Clear the current prompt, but app component button stays available
        // for when the next beforeinstallprompt event fires
        const tempPrompt = this.deferredPrompt;
        this.deferredPrompt = null;
        
        // If user dismissed, the app component button should remain available
        // for future installation attempts
        if (tempPrompt && !this.isPwaInstalled()) {
          // Keep app component button available
        }
        return false;
      }

    } 
    return false;
  }

  dismissHomePrompt(): void {
    this._showInstallInHome.next(false);
    localStorage.setItem('home-pwa-dismissed', 'true');
    // Note: App component install button remains available
  }

  // Legacy method for backward compatibility
  dismissPwa(): void {
    this.dismissHomePrompt();
  }

  // Reset home component dismissal (useful for testing or user preference reset)
  resetHomePromptDismissal(): void {
    localStorage.removeItem('home-pwa-dismissed');
    // Re-enable home component if installation is still possible
    if (this.deferredPrompt && !this.isPwaInstalled()) {
      this._showInstallInHome.next(true);
    }
  }

  canInstall(): boolean {
    return this.deferredPrompt !== null;
  }

  /**
   * Re-check button visibility after login or policy acceptance
   * Buttons are shown regardless of login/policy status, eligibility is checked on click
   */
  recheckEligibility(): void {
    if (!this.canShowInstallOptions() || this.isPwaInstalled()) {
      this._showInstallInApp.next(false);
      this._showInstallInHome.next(false);
      return;
    }

    const homePwaDismissed = localStorage.getItem('home-pwa-dismissed');

    // Check if we have a deferred prompt or if this is a browser that doesn't support it
    if (this.deferredPrompt || this.nativePromptUnsupported()) {
      this._showInstallInApp.next(true);
      if (!homePwaDismissed) {
        this._showInstallInHome.next(true);
      }
    }
  }

  getInstallInstructions(): string {
    const userAgent = navigator.userAgent.toLowerCase();

    if (userAgent.includes('edg')) {
      return 'Aby zainstalować aplikację w Edge:\n\n' +
             '1. Kliknij ikonę instalacji (+) w pasku adresu\n' +
             'lub\n' +
             '1. Kliknij menu (...) przy adresie strony\n' +
             '2. Wybierz "Aplikacje" → "Zainstaluj..."\n' +
             '3. Potwierdź instalację';
    } else if (userAgent.includes('opr') || userAgent.includes('opera')) {
      return 'Aby zainstalować aplikację w Opera:\n\n' +
             '1. Kliknij ikonę instalacji w pasku adresu\n' +
             'lub\n' +
             '1. Kliknij menu Opera w lewym górnym rogu (⋮)\n' +
             '2. Wybierz "Zainstaluj..." lub "Dodaj do ekranu głównego"\n' +
             '3. Potwierdź instalację';
    } else if (userAgent.includes('firefox') && userAgent.includes('android')) {
      return 'Aby zainstalować aplikację w Firefox na Androidzie:\n\n' +
             '1. Kliknij ikonke (⋮) znajdującą się przy adresie strony\n' +
             '2. Wybierz / rozwiń menu "Więcej"\n' +
             '3. Wybierz "Dodaj aplikację do ekranu głównego" lub ikonę domku\n' +
             '4. Potwierdź instalację\n\n' +
             'Lub kliknij ikonę instalacji w pasku adresu.';
    } else if ((userAgent.includes('chrome') || userAgent.includes('crios')) && userAgent.includes('iphone')) {
      return 'Aby zainstalować aplikację w Chrome na iPhone:\n\n' +
             '1. Kliknij przycisk "Udostępnij" w dolnym pasku\n' +
             '2. Przewiń i wybierz "Dodaj do ekranu głównego"\n' +
             '3. Potwierdź przyciskiem "Dodaj"';
    } else if (userAgent.includes('safari') && userAgent.includes('iphone')) {
      return 'Aby zainstalować aplikację w Safari na iPhone:\n\n' +
             '1. Kliknij przycisk "Udostępnij" (□↗)\n' +
             '2. Przewiń w dół i wybierz "Dodaj do ekranu głównego"\n' +
             '3. Nadaj nazwę i dotknij "Dodaj"';
    } else if ((userAgent.includes('chrome') || userAgent.includes('crios')) && !/edg|opr|opera/i.test(userAgent)) {
      return 'Aby zainstalować aplikację w Chrome na Androidzie:\n\n' +
             '1. Kliknij ikonkę znajdującą się w pasku adres "Instaluj ZGrane Grono"\n\n' +
             '2. Potwierdź instalację\n\n';
    } else {
      return 'Twoja przeglądarka nie obsługuje instalacji PWA.\n\n' +
             'Otwórz stronę w Chrome, Firefox, Edge lub Safari ' +
             'lub użyj ikon instalacji (+) w menu lub pasku adresu przeglądarki (jeśli takie istnieją).';
    }
  }

  async clearData(versionToPreserve?: string): Promise<void> {
    try {
      try {
        // Preserve version tracking to prevent endless reload loops
        const lastReloadedVersion = versionToPreserve || localStorage.getItem('lastReloadedVersion');
        const forceMobileMode = localStorage.getItem('forceMobileMode');
        const deviceIdParam = localStorage.getItem('deviceIdParam');

        this.snackBar.open('Trwa czyszczenie pamięci aplikacji...', '', {
          duration: 2500,
          panelClass: ['success-snackbar'],
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
        });
        
        //wait for 2 seconds to ensure any ongoing operations are completed before clearing storage
        await new Promise(resolve => setTimeout(resolve, 5000));

        localStorage.clear();
        sessionStorage.clear();
        
        // Restore the version tracking flag (use the passed version or the old one)
        if (lastReloadedVersion) {
          localStorage.setItem('lastReloadedVersion', lastReloadedVersion);
        }
        if (forceMobileMode) {
          localStorage.setItem('forceMobileMode', forceMobileMode);
        }
        if (deviceIdParam) {
          localStorage.setItem('deviceIdParam', deviceIdParam);
        } 
      } catch (e) {
        console.warn('PwaService: Error clearing storage:', e);
      }

      if ('caches' in window) {
        try {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map(name => caches.delete(name)));
        } catch (e) {
          console.warn('PwaService: Error clearing caches:', e);
        }
      }

      if ('indexedDB' in window) {
        try {
          const idb = (window as any).indexedDB;
          if (idb && typeof idb.databases === 'function') {
            const dbs = await idb.databases();
            await Promise.all(dbs.map((db: any) => db.name ? idb.deleteDatabase(db.name) : Promise.resolve()));
          }
        } catch (e) {
          console.warn('PwaService: Error clearing indexedDB:', e);
        }
      }

      if ('serviceWorker' in navigator) {
        try {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map(r => r.unregister()));
        } catch (e) {
          console.warn('PwaService: Error unregistering service workers:', e);
        }
      }

      this.snackBar.open('Wyczyszczono pamięć aplikacji. Trwa ponowne ładowanie...', '', {
        duration: 2500,
        panelClass: ['success-snackbar'],
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      });

      setTimeout(() => window.location.reload(), 1200);
    } catch (error) {
      console.error('PwaService.clearData error', error);
      this.snackBar.open('Wystąpił błąd podczas czyszczenia pamięci.', 'Zamknij', {
        duration: 4000,
        panelClass: ['warning-snackbar'],
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      });
    }
  }
}